// web/src/components/cumulative_card.ts
//
// Cumulative-Task render layer (UI-01).
//
// Layout: vertical column with consecutive-image dedup at top (D-01 explicit
// string compare), followed by a single text row carrying the most recent
// task's text_effect. A new task with text_effect REPLACES the previously
// displayed text; a new task without text_effect leaves the previous text
// visible (the latest non-empty text_effect in the stack wins).
//
// The cumulative stack lives in card.ts (taskStack: TaskNextState[]);
// this component is a pure render function — caller manages the stack.

import { animate, type DOMKeyframesDefinition } from "motion";
import type { TaskNextState } from "../types/api.js";
import { renderTextEffect } from "./text_effect.js";

/**
 * Render a cumulative stack of Tasks into the given container.
 * Image dedup: if consecutive Tasks share image_url, only one <img> is
 * emitted (D-01 explicit string compare — not assumed equality).
 * Text: only the most recent task's text_effect is rendered. If the
 * latest task has no text_effect, the search falls back to earlier tasks
 * so prior text persists until replaced.
 */
export function renderStack(
  stack: TaskNextState[],
  container: HTMLElement,
  _themeColor: string,
): void {
  container.replaceChildren();

  const column = document.createElement("div");
  column.style.cssText =
    "display:flex;flex-direction:column;align-items:center;" +
    "gap:var(--space-md, 16px);width:100%;";
  container.appendChild(column);

  // Walk newest → oldest; latest non-empty text_effect wins.
  let effectiveTextEffect: TaskNextState["text_effect"] | undefined;
  for (let i = stack.length - 1; i >= 0; i--) {
    const task = stack[i];
    if (task?.text_effect) {
      effectiveTextEffect = task.text_effect;
      break;
    }
  }

  // Image cards with no text effect (Game 1) get a generous vertical budget
  // so the picture actually fills the card-content track instead of floating
  // in whitespace. When the stack also carries text rows (Games 4/5) the
  // image yields room so the column doesn't overflow the clipped container.
  const imgMaxHeight = effectiveTextEffect ? "40vh" : "72vh";

  // D-01: explicit string compare on consecutive image_urls.
  let lastImageURL = "";
  for (const task of stack) {
    if (task.image_url && task.image_url !== lastImageURL) {
      const img = document.createElement("img");
      img.src = task.image_url;
      img.alt = "";
      img.style.cssText =
        `max-width:90vw;max-height:${imgMaxHeight};object-fit:contain;display:block;`;
      column.appendChild(img);
      animate(
        img as Element,
        { opacity: [0, 1] } as DOMKeyframesDefinition,
        { duration: 0.2, ease: "easeOut" },
      );
      lastImageURL = task.image_url;
    }
  }

  if (effectiveTextEffect) {
    const row = document.createElement("div");
    row.style.cssText = "display:flex;justify-content:center;width:100%;";
    column.appendChild(row);
    renderTextEffect(
      effectiveTextEffect.text,
      effectiveTextEffect.type,
      effectiveTextEffect.letter_indexes,
      row,
    );
  }
}
